import { Component, Input } from '@angular/core';
import { Observable, combineLatest, map, tap } from 'rxjs';
import { DeviceStore } from 'src/app/stores/component/device-store';
import { sanitizeToDate } from 'src/app/shared/helpers/formatDates';
import { enumToArray } from 'src/app/shared/helpers/enumToArray';
import { TranslocoService, translate } from '@jsverse/transloco';
import { DevicePollution, DeviceWear, IService, IUpdateServiceIdentifiedRequest, IUpdateServiceStatusRequest, ServiceStatus } from 'src/app/devices/models/service';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, UntypedFormControl } from '@angular/forms';
import { IServicePart, IUpdateServicePart } from 'src/app/devices/models/service-part';
import { ActivatedRoute, Router } from '@angular/router';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { resizeImageTo1920 } from 'src/app/shared/helpers/image-scaling';
import { AttachmentStore } from 'src/app/stores/component/attachment-store';
import { IServiceAttachment, IUpdateServiceAttachment } from 'src/app/devices/models/service-attachment';
import { IDeleteDeviceToRevisionRequest } from 'src/app/revisions/models/revision';
import { AttachmentService } from 'src/app/shared/services/attachment.service';
import { HttpResponse } from '@angular/common/http';
import { IBaseGetByRevisionRequest, IBaseSearchRequest, IFilter, PageSize } from 'src/app/core/models/base';
import { caretAltLeftIcon, caretAltToLeftIcon, caretAltRightIcon, caretAltToRightIcon } from '@progress/kendo-svg-icons';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';



@Component({
    selector: 'app-manage-devices',
    templateUrl: './manage-devices.component.html',
    styleUrl: './manage-devices.component.scss',
    standalone: false
})
export class ManageDevicesComponent {

  ////////////////
  // variables //
  //////////////

  @Input() revisionId;
  @Input() revisionStatus;
  @Input() companyId;
  @Input() tenantOnRevision;

  vm$: Observable<any>;
  serviceForms: { [serviceId: string]: FormGroup } = {};
  deviceWearArray: Array<{ text: string, value: DeviceWear }>;
  devicePollutionArray: Array<{ text: string, value: DevicePollution }>;
  serviceStatus: Array<{ text: string, value: ServiceStatus }>;
  baseUrl: string;
  cancelMessage: string = '';
  currentFilters: IFilter | null = null;
  serviceOpen: Record<number, boolean> = {};

  // temporary variables used inside dialogs
  currentServiceId: string = null;
  currentDeviceId: string = null;
  currentPartCtrl: FormGroup | null = null;
  deviceToDelete: string = null;


  // variables for pagination
  currentPage: number = 1;
  pageSize: number = 25;
  totalPages: number = 0;
  totalCount: number = 0;
  hasNextPage: boolean = false;
  hasPreviousPage: boolean = false;

  // Make icons available to template
  public caretAltLeftIcon = caretAltLeftIcon;
  public caretAltToLeftIcon = caretAltToLeftIcon;
  public caretAltRightIcon = caretAltRightIcon;
  public caretAltToRightIcon = caretAltToRightIcon;

  constructor(
    private deviceStore: DeviceStore,
    private fb: FormBuilder,
    private translocoService: TranslocoService,
    private router: Router,
    private attachmentStore: AttachmentStore,
    private attachmentService: AttachmentService,
    private route: ActivatedRoute
  ) {

    this.vm$ = combineLatest([
      this.deviceStore.devices$.pipe(
        tap(devices => {
          devices.forEach(device => {
            device.services.forEach(service => {
              this.serviceForms[service.id] = this.createServiceForm(service);
            });
          });
        })
      ),
      this.deviceStore.pagination$.pipe(
        tap(pagination => {
          if (pagination) {
          this.pageSize = pagination.pageSize
          this.hasNextPage = pagination.hasNextPage
          this.hasPreviousPage = pagination.hasPreviousPage
          this.currentPage = pagination.currentPage
          this.totalPages = pagination.totalPages
          this.totalCount = pagination.totalCount
          }
        })
      ),
      this.attachmentStore.isLoading$
    ]).pipe(
      map(([
        devices,
        devicePagination,
        isUploading
      ]) => ({
        devices,
        devicePagination,
        isUploading
      }))
    );

  }

  ngOnInit(): void {
    this.deviceStore.getDevicesByRevision({
      revisionId: this.revisionId,
      pageNumber: 0,
      pageSize: PageSize.Small,
    });
    this.initEnums();
    this.baseUrl = this.router.url.split('/')[0] + '/devices-admin/';
  }


  ///////////////
  // Settings //
  /////////////


  // Upload file type restrictions
  serviceRestrictions: FileRestrictions = {
    allowedExtensions: [".jpg", ".jpeg", ".png"],
  };




  ////////////
  // Forms //
  //////////


  // Service form creation
  createServiceForm(service: IService): FormGroup {
    return this.fb.group({
      id: [service.id],
      deviceWear: [service.deviceWear],
      devicePollution: [service.devicePollution],
      remark: [service.remark],
      cancellationMessage: [service.cancellationMessage],
      status: [service.status],
      parts: this.fb.array(service.serviceParts?.map(part => this.createPartForm(part))),
      attachments: this.fb.array(
        service.serviceAttachments?.map((attachment, index) => {
          const existingAttachment = this.getAttachments(service.id)?.value?.[index];

          return this.fb.group({
            id: [attachment.id],
            showInReport: [attachment.showInReport],
            attachmentId: [attachment.attachment.id],
            fileName: [attachment.attachment.fileName],

            // KEEP ALREADY DOWNLOADED IMAGE
            downloadedImage: existingAttachment?.downloadedImage || null
          });
        }) || []
      )
    });
  }
  
  // Service part form creation
  createPartForm(part: IServicePart): FormGroup {
    return this.fb.group({
      id: [part.id],
      amountUsed: [part.amountUsed],
      isUsed: [part.isUsed],
      productName: [part.wearingPart.product.name],
      productInternalNumber: [part.wearingPart.product.internalNumber],
      productQuantity: [part.wearingPart.quantity],
      productIsActive: [part.wearingPart.product.isActive],
      productInactiveReason: [part.wearingPart.product.inactiveReason]
    });
  }

  // Attachment form creation
  createAttachmentForm(attachment: IServiceAttachment): FormGroup {
    return this.fb.group({
      id: [attachment?.id],
      showInReport: [attachment?.showInReport],
      attachmentId: [attachment?.attachment.id],
      fileName: [attachment?.attachment?.fileName],
      downloadedImage: null
    });
  }

  // Create filter form
  filterForm = this.fb.group({
    keyword: new FormControl(null),
    revisionStatus: new UntypedFormControl(null),
  });
  

  // parts getter
  getParts(serviceId: string): FormArray {
    return this.serviceForms[serviceId].get('parts') as FormArray;
  }

  // attachments getter
  getAttachments(serviceId: string): FormArray {
    return (this.serviceForms?.[serviceId]?.get('attachments') as FormArray) ?? null;
  }

  get currentAmountUsedControl(): FormControl {
    return this.currentPartCtrl.get('amountUsed') as FormControl;
  }


  //////////////////////
  // Update function //
  ////////////////////

  // track changes of wear dropdown
  onWearChange(newValue: number, service: IService, ) {
    this.updateServiceField(service, { deviceWear: newValue });
  }

  // track changes of pollution dropdown
  onPollutionChange(newValue: number, service: IService, ) {
    this.updateServiceField(service, { devicePollution: newValue });
  }

  // track changes of description field
  saveRemark(newValue: string, service: IService) {
    this.updateServiceField(service, { description: newValue });
  }

  // update service field with changes
  private updateServiceField(service: IService, patch: Partial<IService>) {
    // 1. Patch the form locally
    this.serviceForms[service.id].patchValue(patch);

    const request = {
      id: service.id,
      deviceWear: this.serviceForms[service.id].get('deviceWear')?.value,
      description: this.serviceForms[service.id].get('description')?.value,
      dateScheduled: service.dateScheduled,
      dateExecuted: service.dateExecuted,
      devicePollution: this.serviceForms[service.id].get('devicePollution')?.value,
      remark: this.serviceForms[service.id].get('remark')?.value,
      cancellationMessage: service.cancellationMessage
    };

    this.deviceStore.updateService({request});
  }

  // unlink device with revision
  removeDevice(deviceId: string): void {
    if(this.revisionStatus.isFinalStatus == false) {

      const request: IDeleteDeviceToRevisionRequest = {
        id: this.revisionId,
        deviceId: deviceId
      }

      this.deviceStore.removeDeviceFromRevision({
        request,
        revisionId: this.revisionId,
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        advancedFilter: this.currentFilters,
        serviceStatus: this.filterForm.value.revisionStatus
      });
    }
  }

  // mark wearing part as used
  public markServicePartDialogOpen(parts: FormArray, i: number, deviceId: string): void {
    if (this.revisionStatus?.isFinalStatus === true) return;

    const part = parts.at(i) as FormGroup;

    // Block checking inactive products (unchecking is still allowed)
    if (part.get('productIsActive')?.value === false && !part.get('isUsed')?.value) return;

    if (!part.get('isUsed')?.value) {
      this.markServicePartDialogOpened = true;

      // store reference to part and device
      this.currentPartCtrl = part;
      this.currentDeviceId = deviceId;

      // prefill amountUsed
      part.get('amountUsed')?.setValue(part.value.productQuantity);
    } else {
      const updateRequest: IUpdateServicePart = {
        id: part.value.id,
        isUsed: false,
        amountUsed: 0
      };

      this.deviceStore.updateServicePart({
        request: updateRequest,
        deviceId: deviceId,
        onSuccess: () => {
          part.get('amountUsed')?.setValue(0, { emitEvent: true });
        }
      });
    }
  }

  completeService(serviceId: string, deviceId: string) {
    const request: IUpdateServiceStatusRequest = {
      id: serviceId,
      status: ServiceStatus.Maintained,
      revisionId: this.revisionId,
    }

    this.deviceStore.updateServiceStatus({
      request: request, 
      deviceId: deviceId, 
      onSuccess: () => {

      }
    });
  }

  // cancel service
  cancelService(serviceId: string, deviceId: string) {
    const request: IUpdateServiceStatusRequest = {
      id: serviceId,
      status: ServiceStatus.NotMaintained,
      revisionId: this.revisionId,
      cancellationMessage: this.cancelMessage
    }

    this.deviceStore.updateServiceStatus({
      request: request, 
      deviceId: deviceId, 
      onSuccess: () => {
      }
    });
  }

  async uploadServiceAttachment(event: UploadEvent, serviceId: string, deviceId: string) {
    event.preventDefault();

    // Create an array to store all FormData objects
    const formDataArray: FormData[] = [];

    // Loop through the files and upload each one
    for (const file of event.files) {
      const formData = new FormData();
      
      // Resize image
      const processedBlob = await resizeImageTo1920(file.rawFile);
      const processedFile = new File([processedBlob], file.name, { type: file.rawFile.type });

      formData.append('file', processedFile, file.name);

      // Append optional IDs to FormData if they are provided
      if (serviceId) {
        formData.append('serviceId', serviceId);
      }

      formData.append('tenantId', this.tenantOnRevision);
      
      formDataArray.push(formData);
    }
    
    if (formDataArray.length === 0) return;

    // Upload all files
    this.deviceStore.addAttachments({
      attachments: formDataArray,
      onSuccess: async (uploaded) => {
        for (const attachment of uploaded) {
          // Immediately set placeholder in UI
          
          const serviceGroup = this.serviceForms[attachment.serviceId] as FormGroup;
          if (!serviceGroup) continue;

          const attachmentsArray = this.getAttachments(attachment.serviceId)?.value || [];
          const targetAttachment = attachmentsArray.find((a: any) => a.id === attachment.id);
          if (targetAttachment) {
            targetAttachment.downloadedImage = null; // show placeholder
            serviceGroup.get('attachments')?.patchValue([...attachmentsArray]);
          }

          // Poll until thumbnail is ready
          const ready = await this.attachmentService.waitForThumbnail(attachment.attachment.id, 15, 1000); // 15 retries, 1s interval
          if (!ready) continue;

          // Download thumbnail
          this.attachmentService.downloadThumbnail({ attachmentId: attachment.attachment.id })
            .subscribe((res: HttpResponse<Blob>) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const downloadedImage = reader.result as string;

                const attachmentsArray = serviceGroup.get('attachments')?.value || [];
                const targetAttachment = attachmentsArray.find((a: any) => a.id === attachment.id);
                if (targetAttachment) {
                  targetAttachment.downloadedImage = downloadedImage;
                  serviceGroup.get('attachments')?.patchValue([...attachmentsArray]);
                }
              };
              if (res.body) reader.readAsDataURL(res.body);
            });
        }
      }
    });

  }

  deleteAttachment(attachmentId: any, serviceId: string) {
    this.deviceStore.removeAttachment({
      attachmentId: attachmentId,
      serviceId: serviceId,
      onSuccess: () => {
      }
    });
  }

  downloadAttachment(attachment: any) {
    this.attachmentService.download({attachmentId: attachment.attachmentId}).subscribe(response => downloadBlob(response));
  }

  // when device is selected or deselected for revision report
  // device identified on site (saved immediately)
  onIdentifiedChange(event, serviceId: string, deviceId: string) {
    const request: IUpdateServiceIdentifiedRequest = {
      id: serviceId,
      identified: event.target.checked === true
    };

    this.deviceStore.updateServiceIdentified({ request, deviceId });
  }

  onCheckboxChange(event, imageId: string, deviceId: string) {
    var request: IUpdateServiceAttachment;

    if ( event.target.checked ) {
      request = {
        id: imageId,
        showInReport: true
      }
    } else {
      request = {
        id: imageId,
        showInReport: false
      }
    }

    this.deviceStore.updateServiceAttachment({request, deviceId});
  }

  ///////////////////
  // UI Functions //
  /////////////////

  // toggle extended service view
  toggle(event: Event, i: any, serviceId: string) {
    // Toggle the state - Angular binding will handle the classes
    this.serviceOpen[serviceId] = !this.serviceOpen[serviceId];
  
    // Only download attachments when opening
    if (this.serviceOpen[serviceId]) {
      const attachments = this.getAttachments(serviceId)?.value;
      attachments?.forEach(attachment => {
        if (!attachment.downloadedImage) {
          this.attachmentService.downloadThumbnail({ attachmentId: attachment.attachmentId })
            .subscribe((res: HttpResponse<Blob>) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                attachment.downloadedImage = reader.result as string;
              };
              if (res.body) {
                reader.readAsDataURL(res.body);
              }
            });
        }
      });
    }
  }



  /////////////
  // Filter //
  ///////////




  onSubmit(request: IBaseSearchRequest){
    var formValues = this.filterForm.value;

    // Trim the keyword before using
    const keyword = formValues.keyword ? formValues.keyword.trim() : null;

    // Fields to search for the keyword
    const searchFields = ['serialNumber', 'tagNumber'];

    // Create keyword filters
    const keywordFilters: IFilter[] = keyword 
        ? searchFields.map(field => ({ field, operator: 'contains', value: keyword })) 
        : [];

    var otherFilters: IFilter[] =[]

    // Combine keyword filters with 'or' logic if there are any
    const combinedKeywordFilter: IFilter = { logic: 'or', filters: keywordFilters } 

    // Combine keyword filters with other filters using 'and' logic
    const allFilters: IFilter[] = combinedKeywordFilter.filters.length > 0
      ? [combinedKeywordFilter, ...otherFilters]
      : otherFilters;

    // Store the current filters
    if (allFilters.length > 0) {
      this.currentFilters = {
        logic: "and",
        filters: allFilters
      };
    } else {
      this.currentFilters = null;
    }

    // Reset to page 1 and load devices with filters
    this.currentPage = 1;
    this.loadDevices(formValues.revisionStatus);
  }

  // Helper method to apply filters and load devices
  private applyFiltersAndLoadDevices(): void {
    const formValues = this.filterForm.value;
    
    // Build filters from current form values
    const keyword = formValues.keyword ? formValues.keyword.trim() : null;
    const searchFields = ['serialNumber', 'tagNumber'];
    const keywordFilters: IFilter[] = keyword 
      ? searchFields.map(field => ({ field, operator: 'contains', value: keyword })) 
      : [];

    const otherFilters: IFilter[] = [];

    const combinedKeywordFilter: IFilter = { logic: 'or', filters: keywordFilters };
    const allFilters: IFilter[] = combinedKeywordFilter.filters.length > 0
      ? [combinedKeywordFilter, ...otherFilters]
      : otherFilters;

    if (allFilters.length > 0) {
      this.currentFilters = {
        logic: "and",
        filters: allFilters
      };
    } else {
      this.currentFilters = null;
    }

    this.loadDevices(formValues.revisionStatus);
  }

  resetFilter(): void {
    // Reset form
    this.filterForm.reset();
    
    // Clear current filters
    this.currentFilters = null;
    
    // Reset to page 1
    this.currentPage = 1;
    
    // Load devices without filters
    this.loadDevices();

    // Reset url params
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      queryParamsHandling: '',
    });
  }


  /////////////////
  // Pagination //
  ///////////////

  // function to load devices after pagination changes
  loadDevices(serviceStatus?: number): void {
    this.deviceStore.getDevicesByRevision({
      revisionId: this.revisionId,
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      advancedFilter: this.currentFilters,
      serviceStatus
    });
  }
  
  // pagination function
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadDevices(this.filterForm.value.revisionStatus);
    }
  }

  // pagination function
  nextPage(): void {
    if (this.hasNextPage) {
      this.currentPage++;
      this.loadDevices(this.filterForm.value.revisionStatus);
    }
  }

  // pagination function
  previousPage(): void {
    if (this.hasPreviousPage) {
      this.currentPage--;
      this.loadDevices(this.filterForm.value.revisionStatus);
    }
  }

  // pagination function
  onPageSizeChange(newSize: number): void {
    if (this.pageSize === newSize) return;
    
    this.pageSize = newSize;
    this.currentPage = 1;

    this.loadDevices(this.filterForm.value.revisionStatus);
  }

  // Helper method to generate page numbers for pagination UI
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }



  //////////////
  // Dialogs //
  ////////////


  // complete service
  public completeServiceDialogOpened = false;

  public completeServiceDialogClose(status: string): void {
    this.completeServiceDialogOpened = false;

    if(status == 'confirm') {
      this.completeService(this.currentServiceId, this.currentDeviceId);
    }
    
    this.currentServiceId = null;
    this.currentDeviceId = null;
  }

  public completeServiceDialogOpen(serviceId: string, deviceId: string): void {
    this.completeServiceDialogOpened = true;
    this.currentServiceId = serviceId;
    this.currentDeviceId = deviceId;
  }  


  // cancel service
  public cancelServiceDialogOpened = false;

  public cancelServiceDialogClose(status: string): void {
    this.cancelServiceDialogOpened = false;

    if(status == 'confirm') {
      this.cancelService(this.currentServiceId, this.currentDeviceId);
    }

    this.currentServiceId = null;
    this.currentDeviceId = null;
  }

  public cancelServiceDialogOpen(serviceId: string, deviceId: string): void {
    this.cancelServiceDialogOpened = true;
    this.currentServiceId = serviceId;
    this.currentDeviceId = deviceId;
  }


  // delete confirmation dialog
  public deletionDialogOpened = false;

  public deletionDialogClose(status: string): void {
    this.deletionDialogOpened = false;

    if(status == 'delete') {
      this.removeDevice(this.deviceToDelete);
    }

    this.deviceToDelete = null;
  }

  public deletionDialogOpen(deviceId: string): void {
    this.deletionDialogOpened = true;
    this.deviceToDelete = deviceId;
  }

  
  // mark wearing part as used
  public markServicePartDialogOpened = false;

  public markServicePartDialogClose(event, status: string): void {
    if (!this.currentPartCtrl || !this.currentDeviceId) return;

    if (status === 'confirm') {
      const updateRequest: IUpdateServicePart = {
        id: this.currentPartCtrl.value.id,
        isUsed: this.currentPartCtrl.get('isUsed')?.value,
        amountUsed: this.currentPartCtrl.get('amountUsed')?.value
      };
      this.deviceStore.updateServicePart({
        request: updateRequest,
        deviceId: this.currentDeviceId
      });
    } else {
      this.currentPartCtrl.get('isUsed')?.setValue(false);
      this.currentPartCtrl.get('amountUsed')?.setValue(0);
    }

    this.currentPartCtrl = null;
    this.currentDeviceId = null;
    this.markServicePartDialogOpened = false;
  }


  //////////////
  // Helpers //
  ////////////

  // format service date
  formatDeviceDate(input: any) {
    return sanitizeToDate(input);
  }

  // convert status number to translations
  convertServiceStatusEnum(statusValue: number): string {
    const statusArray = enumToArray(ServiceStatus, 'revisions.detail.device.serviceStatusEnums.');
    const found = statusArray.find(s => s.id === statusValue);
    return found ? found.name : translate('revisions.detail.device.enumUnknown');
  }

  // init form data source for enum dropdowns
  initEnums() {  
    this.serviceStatus = Object.keys(ServiceStatus)
    .filter(key => isNaN(Number(key)))
    .map(key => ({
        text: this.translocoService.translate(`revisions.detail.device.serviceStatusEnums.${key}`),
        value: ServiceStatus[key as keyof typeof ServiceStatus]
  }));

    this.deviceWearArray = Object.keys(DeviceWear)
      .filter(key => isNaN(Number(key)))
      .map(key => ({
          text: this.translocoService.translate(`revisions.detail.device.deviceWearEnums.${key}`),
          value: DeviceWear[key as keyof typeof DeviceWear]
    }));

    this.devicePollutionArray = Object.keys(DevicePollution)
      .filter(key => isNaN(Number(key)))
      .map(key => ({
          text: this.translocoService.translate(`revisions.detail.device.devicePollutionEnums.${key}`),
          value: DevicePollution[key as keyof typeof DevicePollution]
    }));
  }  
}